/**
 * manpower/useManpower/useManpowerSync.js - the calendar's server sync:
 * apply a server copy with this tab's changes on top, read it, retry a failed
 * save, and sync() - the one place a save happens.
 * Moved verbatim out of useManpower.js, which calls it at the same point
 * (same hook order) with the state and refs it reads.
 */
import { useCallback } from 'react';
import { LS_EDITS_KEY, LS_CUSTOM_PROJ_KEY, LS_COLOR_KEY } from '../manpowerConstants';
import {
  copyChanges,
  unionChanges,
  rebaseDays,
  writeLS,
  LS_DETAILS_KEY,
  rebaseNames,
  rebaseColors,
  nextRetryMs,
  buildSave,
  settle,
  hasChanges,
  SAVE_DEBOUNCE_MS,
} from './scheduleChanges';
import { openCalendars, closedWithChanges } from './openCalendars';

export function useManpowerSync({
  changesRef,
  setSchedState,
  latestRef,
  setCustomProjectsState,
  setPaletteState,
  detailsLoadedRef,
  loadedRef,
  setLoaded,
  aliveRef,
  readSeqRef,
  landedRef,
  shownSeqRef,
  rereadRef,
  setSyncStatus,
  retiredRef,
  retryMsRef,
  schedule,
  busyRef,
  saveFailedRef,
  onSavedRef,
  reloadRef,
  closedRef,
  timerRef,
  syncRef,
}) {
  // The server's copy becomes the one on screen (and in the offline cache),
  // with this tab's unsaved changes put back on top. `held` is the changes as
  // they stood when the copy was judged current: a save of ours that lands
  // before React runs these updates no longer counts as unsaved, yet the copy
  // may still be from before it.
  const applyServer = useCallback(
    (d) => {
      const held = copyChanges(changesRef.current);
      const ours = () => unionChanges(held, changesRef.current);
      setSchedState((cur) => {
        const next = rebaseDays(d, cur, ours().days);
        writeLS(LS_EDITS_KEY, next.edits);
        writeLS(LS_DETAILS_KEY, next.details);
        latestRef.current = { ...latestRef.current, edits: next.edits, details: next.details };
        return next;
      });
      setCustomProjectsState((cur) => {
        const next = rebaseNames(d.customProjects || [], cur, ours());
        writeLS(LS_CUSTOM_PROJ_KEY, next);
        latestRef.current = { ...latestRef.current, customProjects: next };
        return next;
      });
      setPaletteState((cur) => {
        const next = rebaseColors(d.colors || {}, cur, ours());
        writeLS(LS_COLOR_KEY, next);
        latestRef.current = { ...latestRef.current, colors: next };
        return next;
      });
      detailsLoadedRef.current = !!d.details;
      loadedRef.current = true;
      setLoaded(true);
    },
    [changesRef, detailsLoadedRef, latestRef, loadedRef, setCustomProjectsState, setLoaded, setPaletteState, setSchedState]
  );

  // Read the server's copy and show it, ours on top. True once a copy is on
  // screen (this one, or a newer one already was); throws when the read fails.
  const readServer = useCallback(async () => {
    for (let tries = 0; tries < 5 && aliveRef.current; tries += 1) {
      const seq = ++readSeqRef.current;
      const landed = landedRef.current;
      const r = await fetch('/api/manpower/edits');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = (await r.json()) || {};
      if (seq < shownSeqRef.current) return true;
      // A save of ours went through while this read was out: the copy may be
      // from before it, and those days no longer count as unsaved. Again.
      if (landedRef.current !== landed) continue;
      shownSeqRef.current = seq;
      applyServer(d);
      return true;
    }
    rereadRef.current = true;
    return false;
  }, [applyServer, aliveRef, landedRef, readSeqRef, rereadRef, shownSeqRef]);

  // A save (or the first read) did not work: say so (`status`, see
  // syncStatus) and try again - after 2 s, then 4, 8 ... every 60 s - until it
  // does, open or closed. Not once a newer calendar took the changes over, nor
  // when closed before the server's copy ever came (closed, it cannot read
  // one): those changes wait for the next calendar opened here.
  const failed = useCallback(
    (status = 'error') => {
      setSyncStatus(status);
      if (retiredRef.current || (!aliveRef.current && !loadedRef.current)) return;
      retryMsRef.current = nextRetryMs(retryMsRef.current);
      schedule(retryMsRef.current, true);
    },
    [schedule, aliveRef, loadedRef, retiredRef, retryMsRef, setSyncStatus]
  );

  // Send what this tab changed - the one place a save happens.
  const sync = useCallback(async () => {
    if (busyRef.current) return; // the save that is out sends the rest when it lands
    if (retiredRef.current) return; // a newer calendar sends these now
    busyRef.current = true;
    try {
      if (!loadedRef.current) {
        try {
          await readServer();
        } catch (e) {
          console.warn('manpower edits GET failed, using localStorage cache:', e.message);
        }
        if (!loadedRef.current) {
          failed();
          return;
        }
        retryMsRef.current = 0;
      }
      const out = buildSave(changesRef.current, latestRef.current, detailsLoadedRef.current);
      if (!out || out.empty) {
        if (out) settle(changesRef.current, out.sent, latestRef.current);
        // nothing left to send, so no save is failing: the error line says
        // "trying again ... until this clears", and here it clears
        if (!hasChanges(changesRef.current)) {
          saveFailedRef.current = false;
          retryMsRef.current = 0;
        }
        setSyncStatus(saveFailedRef.current || 'idle');
        return;
      }
      setSyncStatus((s) => (s === 'error' || s === 'reload' ? s : 'saving'));
      let res;
      try {
        const r = await fetch('/api/manpower/edits', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(out.body),
        });
        if (!r.ok) {
          let code = null;
          if (r.status === 409) {
            try {
              code = ((await r.json()) || {}).code || null;
            } catch {
              /* no JSON body */
            }
          }
          throw Object.assign(new Error(`HTTP ${r.status}`), { code });
        }
        try {
          res = (await r.json()) || {};
        } catch {
          res = {};
        }
      } catch (e) {
        console.warn('manpower edits PATCH failed:', e.message);
        saveFailedRef.current = e.code === 'reload_required' ? 'reload' : 'error';
        failed(saveFailedRef.current);
        return;
      }
      settle(changesRef.current, out.sent, latestRef.current);
      landedRef.current += 1;
      saveFailedRef.current = false;
      retryMsRef.current = 0;
      setSyncStatus('idle');
      // The save can rewrite engineer cards (the calendar -> card sync), and
      // only the caller knows how to refetch those. Kept off the error path:
      // a handler that throws is not a failed save.
      try {
        if (onSavedRef.current) onSavedRef.current(res);
      } catch (e) {
        console.warn('manpower onSaved failed:', e.message);
      }
      for (const reread of [...openCalendars]) if (reread !== reloadRef.current) reread();
      if (rereadRef.current && reloadRef.current) {
        rereadRef.current = false;
        reloadRef.current();
      }
    } finally {
      busyRef.current = false;
      // closed with everything saved: nothing left for a newer calendar to take over
      if (!aliveRef.current && !hasChanges(changesRef.current)) closedWithChanges.delete(closedRef.current);
    }
    // edited again while that save was out: those go next (at once, from a
    // calendar already closed)
    if (!hasChanges(changesRef.current) || timerRef.current) return;
    if (aliveRef.current) schedule(SAVE_DEBOUNCE_MS);
    else if (syncRef.current) syncRef.current();
  }, [
    readServer,
    failed,
    schedule,
    aliveRef,
    busyRef,
    changesRef,
    closedRef,
    detailsLoadedRef,
    landedRef,
    latestRef,
    loadedRef,
    onSavedRef,
    reloadRef,
    rereadRef,
    retiredRef,
    retryMsRef,
    saveFailedRef,
    setSyncStatus,
    syncRef,
    timerRef,
  ]);
  return { sync, readServer };
}
